import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";

// Import the API calls we created
import { fetchChapterActivities } from "@/api/fetchChapterActivities/fetchChapterActivitiesApi";
import {
  FetchChapterActivitiesParams,
  FetchChapterActivitiesResponse,
  ChapterActivity,
} from "@/api/fetchChapterActivities/types";

import { submitChapterActivity } from "@/api/submitChapterActivity/submitChapterActivityApi";
import {
  SubmitChapterActivityRequest,
  SubmitChapterActivityResponse,
} from "@/api/submitChapterActivity/types";

// Slice state
interface ChapterActivityState {
  activities: ChapterActivity[];
  totalCount: number;

  fetchLoading: boolean;
  fetchError: string | null;

  submitLoading: boolean;
  submitError: string | null;
  submitSuccess: boolean;
}

const initialState: ChapterActivityState = {
  activities: [],
  totalCount: 0,

  fetchLoading: false,
  fetchError: null,

  submitLoading: false,
  submitError: null,
  submitSuccess: false,
};

export const chapterActivitySlice = createSlice({
  name: "chapterActivity",
  initialState,
  reducers: {
    // Fetch actions
    setFetchLoading(state, action: PayloadAction<boolean>) {
      state.fetchLoading = action.payload;
    },
    setFetchError(state, action: PayloadAction<string | null>) {
      state.fetchError = action.payload;
    },
    setActivities(state, action: PayloadAction<ChapterActivity[]>) {
      state.activities = action.payload;
    },
    setTotalCount(state, action: PayloadAction<number>) {
      state.totalCount = action.payload;
    },

    // Submit actions
    setSubmitLoading(state, action: PayloadAction<boolean>) {
      state.submitLoading = action.payload;
    },
    setSubmitError(state, action: PayloadAction<string | null>) {
      state.submitError = action.payload;
    },
    setSubmitSuccess(state, action: PayloadAction<boolean>) {
      state.submitSuccess = action.payload;
    },

    // Helper to add a new activity to the list (if needed)
    addNewActivity(state, action: PayloadAction<ChapterActivity>) {
      state.activities.unshift(action.payload);
      state.totalCount += 1;
    },

    // Reset form state (useful after submission)
    resetSubmitState(state) {
      state.submitLoading = false;
      state.submitError = null;
      state.submitSuccess = false;
    },
  },
});

export default chapterActivitySlice.reducer;

export const {
  setFetchLoading,
  setFetchError,
  setActivities,
  setTotalCount,

  setSubmitLoading,
  setSubmitError,
  setSubmitSuccess,

  addNewActivity,
  resetSubmitState,
} = chapterActivitySlice.actions;

// Thunk: Fetch Chapter Activities
export const fetchChapterActivitiesAction =
  (params?: FetchChapterActivitiesParams): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setFetchLoading(true));
    dispatch(setFetchError(null));
    try {
      const result: FetchChapterActivitiesResponse =
        await fetchChapterActivities(params);
      dispatch(setActivities(result.data));
      dispatch(setTotalCount(result.meta?.totalCount ?? 0));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch chapter activities.";
      dispatch(setFetchError(message));
    } finally {
      dispatch(setFetchLoading(false));
    }
  };

// Thunk: Submit Chapter Activity
export const submitChapterActivityAction =
  (payload: SubmitChapterActivityRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setSubmitLoading(true));
    dispatch(setSubmitError(null));
    dispatch(setSubmitSuccess(false));
    try {
      const response: SubmitChapterActivityResponse =
        await submitChapterActivity(payload);

      if (response.data.success) {
        dispatch(setSubmitSuccess(true));
        // Note: Typically, we'd fetch the activities again or add the new one
        // to the list, but we don't have the complete activity object from
        // the response in this case
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to submit chapter activity.";
      dispatch(setSubmitError(message));
    } finally {
      dispatch(setSubmitLoading(false));
    }
  };
